import { QuestionCircleOutlined } from '@ant-design/icons';
import { Collapse, Tooltip } from 'antd';
import { cn } from 'cn';
import { isValidElement, type ReactNode, useState } from 'react';

type ContainerCollapseProps = {
    defaultOpen?: boolean;
    forceRender?: boolean;
};

function ContainerTitle({
    title,
    help,
    className,
}: {
    title: ReactNode;
    help?: string;
    className?: string;
}) {
    return (
        <div className={cn('text-center text-base font-semibold', className)}>
            {title}

            {help ? (
                <Tooltip title={help}>
                    <QuestionCircleOutlined className='ml-2' />
                </Tooltip>
            ) : null}
        </div>
    );
}

function ContainerContent({
    children,
    className,
    centered,
    gap,
}: {
    children?: ReactNode;
    className?: string;
    centered?: boolean;
    gap?: boolean;
}) {
    return (
        <div
            className={cn(
                'p-4',
                centered && 'flex flex-col items-center justify-center',
                gap && 'flex flex-col gap-2',
                className,
            )}
        >
            {children}
        </div>
    );
}

ContainerContent.__isContainerContent = true;

function isContainerContent(element: unknown): element is React.ReactElement {
    return (
        isValidElement(element) &&
        (element.type === ContainerContent ||
            (element.type as typeof ContainerContent).__isContainerContent === true)
    );
}

const defaultProps = {
    defaultOpen: true,
    forceRender: true,
};

function ContainerCollapse({
    collapse,
    titleElement,
    contentElement,
}: {
    collapse: boolean | ContainerCollapseProps;
    titleElement: ReactNode;
    contentElement: ReactNode;
}) {
    const { defaultOpen, forceRender } =
        typeof collapse === 'object' ? { ...defaultProps, ...collapse } : defaultProps;

    const [open, setOpen] = useState(defaultOpen);
    const [previousDefaultOpen, setPreviousDefaultOpen] = useState(defaultOpen);

    if (previousDefaultOpen !== defaultOpen) {
        setPreviousDefaultOpen(defaultOpen);
        setOpen(defaultOpen);
    }

    return (
        <Collapse
            activeKey={open ? [1] : []}
            classNames={{
                body: '!p-0 bg-gray-50',
                header: '!py-1 bg-gray-100',
            }}
            items={[
                {
                    children: contentElement,
                    forceRender,
                    key: 1,
                    label: titleElement,
                },
            ]}
            onChange={(key) => {
                setOpen(key.length > 0);
            }}
            size='small'
        />
    );
}

export function Container({
    className,
    title,
    children,
    collapse,
}: {
    title?: ReactNode;
    children?: ReactNode;
    className?: string;
    collapse?: boolean | ContainerCollapseProps;
}) {
    const titleElement = typeof title === 'string' ? <Container.Title title={title} /> : title;

    const contentElement = isContainerContent(children) ? (
        children
    ) : (
        <ContainerContent>{children}</ContainerContent>
    );

    if (collapse) {
        return (
            <ContainerCollapse
                collapse={collapse}
                contentElement={contentElement}
                titleElement={titleElement}
            />
        );
    }

    return (
        <div className={cn('rounded-md border border-gray-200 bg-gray-50', className)}>
            {title ? (
                <div className='border-b border-gray-200 bg-gray-100 p-1'>{titleElement}</div>
            ) : null}

            {contentElement}
        </div>
    );
}

Container.Title = ContainerTitle;
Container.Content = ContainerContent;
